import React from 'react';

import {Button} from '@kit';

import {useCharacter} from '../character-context';
import type {LimitedUse} from '../model/character';
import {rechargeLabel} from '../model/labels';
import {Marks} from './marks';

import css from './sheet.module.css';

const MAX_MARKS = 10;

type UsageTrackerProps = {
	use: LimitedUse;
};

export const UsageTracker: React.FC<UsageTrackerProps> = ({use}) => {
	const {update} = useCharacter();
	const remaining = use.max - use.spent;

	const setRemaining = (value: number) => {
		const spent = use.max - Math.max(0, Math.min(use.max, value));
		update((current) => ({spentUses: {...current.spentUses, [use.key]: spent}}));
	};

	return (
		<span className={css.usageTracker}>
			{use.max <= MAX_MARKS ? (
				<Marks total={use.max} filled={remaining} label={use.title} shape="square" onChange={setRemaining} />
			) : (
				<UsageStepper remaining={remaining} max={use.max} onChange={setRemaining} />
			)}
			<RechargeNote use={use} />
		</span>
	);
};

const RechargeNote: React.FC<{use: LimitedUse}> = ({use}) => {
	if (!use.recharge) return null;
	const shortRest = use.regainsOnShortRest ? `, короткий отдых: +${use.regainsOnShortRest}` : '';
	const regain = use.regain ? ` (+${use.regain})` : '';
	return (
		<span className={css.rechargeNote}>
			перезарядка: {rechargeLabel(use.recharge)}
			{regain}
			{shortRest}
		</span>
	);
};

type UsageStepperProps = {
	remaining: number;
	max: number;
	onChange(remaining: number): void;
};

const UsageStepper: React.FC<UsageStepperProps> = ({remaining, max, onChange}) => (
	<span className={css.stepper}>
		<Button size="small" appearance="subtle" aria-label="Потратить" onClick={() => onChange(remaining - 1)}>
			−
		</Button>
		<span>
			{remaining} / {max}
		</span>
		<Button size="small" appearance="subtle" aria-label="Вернуть" onClick={() => onChange(remaining + 1)}>
			+
		</Button>
	</span>
);

type UsageCountProps = {
	useKey: string;
	/** что показать, если использования не считаются */
	fallback?: string;
};

export const UsageCount: React.FC<UsageCountProps> = ({useKey, fallback}) => {
	const {character} = useCharacter();
	const use = character.limitedUse(useKey);
	if (use) return <UsageTracker use={use} />;
	return fallback ? <span>{fallback}</span> : null;
};
